/**
 * Music: keys, chords, transposition, Nashville numbers and capo. Charts —
 * every format, ChordPro among them — are in chart.ts.
 *
 * Shared verbatim between the web chart preview, the Music Stand and the
 * server's validation, so a chord is spelled the same way everywhere.
 *
 * ## Spelling is a property of the *key*, not the note
 *
 * F♯ and G♭ are the same pitch and different keys. A chart in G♭ spells its
 * IV as C♭ and its V as D♭; the same chart in F♯ spells them B and C♯. So a
 * transposition decides the spelling from the target key's signature —
 * flat keys spell with flats, sharp keys with sharps — and that is what makes
 * "transpose up a tritone and back" return exactly the chart you started with.
 */

export const PITCH_COUNT = 12

/** Natural letters and the pitch class each names. */
const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const
type Letter = (typeof LETTERS)[number]
const LETTER_PITCH: Record<Letter, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }

const mod12 = (n: number) => ((n % 12) + 12) % 12

/** A note name: a letter and its accidentals ("C", "F#", "Bb", "Cb", "E#"). */
export interface Note {
  letter: Letter
  /** −2..+2: flats negative, sharps positive. */
  accidental: number
}

export function parseNote(text: string): Note | null {
  const match = /^([A-Ga-g])(##|bb|#|b|♯|♭|x)?$/.exec(text.trim())
  if (!match) return null
  const letter = (match[1] ?? 'C').toUpperCase() as Letter
  const acc = match[2] ?? ''
  const accidental =
    acc === '#' || acc === '♯'
      ? 1
      : acc === 'b' || acc === '♭'
        ? -1
        : acc === '##' || acc === 'x'
          ? 2
          : acc === 'bb'
            ? -2
            : 0
  return { letter, accidental }
}

export const pitchOf = (note: Note): number => mod12(LETTER_PITCH[note.letter] + note.accidental)

export function noteName(note: Note, unicode = false): string {
  const sharp = unicode ? '♯' : '#'
  const flat = unicode ? '♭' : 'b'
  const marks =
    note.accidental > 0
      ? sharp.repeat(note.accidental)
      : note.accidental < 0
        ? flat.repeat(-note.accidental)
        : ''
  return `${note.letter}${marks}`
}

// ---- keys -------------------------------------------------------------------------

/**
 * The fifteen major keys a chart can be written in, with the side of the
 * circle of fifths they sit on. C♯, F♯ and C♭, G♭, D♭ are all real — a
 * library imported from a publisher will contain every one.
 */
export const MAJOR_KEYS = [
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'C#',
  'F',
  'Bb',
  'Eb',
  'Ab',
  'Db',
  'Gb',
  'Cb',
] as const
export type MajorKey = (typeof MAJOR_KEYS)[number]

const FLAT_KEYS = new Set<string>(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb', 'Cb'])

/** The keys a key picker offers: one per pitch, the common spelling. */
export const COMMON_KEYS = [
  'C',
  'Db',
  'D',
  'Eb',
  'E',
  'F',
  'F#',
  'G',
  'Ab',
  'A',
  'Bb',
  'B',
] as const

/**
 * A key as written: "G", "F#", "Bbm" (minor keys are spelled by their tonic
 * and take the signature of their relative major).
 */
export interface Key {
  tonic: Note
  minor: boolean
}

export function parseKey(text: string): Key | null {
  const match = /^([A-Ga-g](?:##|bb|#|b|♯|♭)?)(m|min|-)?$/.exec(text.trim())
  if (!match) return null
  const tonic = parseNote(match[1] ?? '')
  return tonic ? { tonic, minor: Boolean(match[2]) } : null
}

export const keyName = (key: Key): string => `${noteName(key.tonic)}${key.minor ? 'm' : ''}`

/** The major key whose signature a key uses (a minor key's relative major). */
function signatureOf(key: Key): Note {
  return key.minor
    ? spellInKey(mod12(pitchOf(key.tonic) + 3), { tonic: key.tonic, minor: false }, 2)
    : key.tonic
}

/** Whether a key's signature is written with flats. */
function usesFlats(key: Key): boolean {
  const major = signatureOf(key)
  const name = noteName(major)
  if (FLAT_KEYS.has(name)) return true
  if ((MAJOR_KEYS as readonly string[]).includes(name)) return false
  return major.accidental < 0
}

/**
 * Spells a pitch as a scale degree of a key: the letter is fixed by the
 * degree, the accidental is whatever makes the pitch. So in G♭ the fourth
 * degree is C♭ (not B), and in F♯ the seventh is E♯ (not F).
 */
function spellInKey(pitch: number, key: Key, degreeLetterSteps: number): Note {
  const tonicIndex = LETTERS.indexOf(key.tonic.letter)
  const letter = LETTERS[(tonicIndex + degreeLetterSteps) % 7] ?? 'C'
  let accidental = mod12(pitch - LETTER_PITCH[letter])
  if (accidental > 6) accidental -= 12
  return { letter, accidental }
}

/** A pitch's plain name, with sharps or with flats. */
function spellingInMajorFor(pitch: number, preferFlats: boolean): string {
  const flats = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']
  const sharps = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
  return (preferFlats ? flats : sharps)[pitch] ?? 'C'
}

/** Semitones from a key's tonic to each diatonic degree (major). */
const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11]

/**
 * Spells a pitch for a key. Diatonic pitches take their degree's letter;
 * chromatic ones take the key's side of the circle (♭ in flat keys, ♯ in
 * sharp keys), on the nearest letter.
 */
export function spellPitch(pitch: number, key: Key): Note {
  const fromTonic = mod12(pitch - pitchOf(key.tonic))
  // Minor keys spell from their own degrees: natural minor.
  const steps = key.minor ? [0, 2, 3, 5, 7, 8, 10] : MAJOR_STEPS
  const degree = steps.indexOf(fromTonic)
  if (degree >= 0) return spellInKey(pitch, key, degree)
  // Chromatic: the plain name on the key's side of the circle — B♭ in F,
  // A♯ in E, and C (not B♯) for the ♭VII of D. Letter-exact spellings like
  // C♭ and E♯ are for the scale's own degrees only.
  const name = spellingInMajorFor(pitch, usesFlats(key))
  return parseNote(name) ?? { letter: 'C', accidental: 0 }
}

// ---- chords -----------------------------------------------------------------------

export interface Chord {
  root: Note
  /** Everything after the root and before a slash: "m7", "sus4", "maj7(#11)". */
  quality: string
  bass: Note | null
}

const CHORD = /^([A-G](?:##|bb|#|b|♯|♭)?)([^/]*)(?:\/([A-G](?:##|bb|#|b|♯|♭)?))?$/

export function parseChord(text: string): Chord | null {
  const match = CHORD.exec(text.trim())
  if (!match) return null
  const root = parseNote(match[1] ?? '')
  if (!root) return null
  const bass = match[3] ? parseNote(match[3]) : null
  return { root, quality: match[2] ?? '', bass }
}

export function chordName(chord: Chord, unicode = false): string {
  return `${noteName(chord.root, unicode)}${chord.quality}${chord.bass ? `/${noteName(chord.bass, unicode)}` : ''}`
}

/**
 * Transposes a chord from one key to another, spelled for the target key.
 * Text that is not a chord (a "N.C.", a "(x2)") is returned unchanged.
 */
export function transposeChord(text: string, from: Key, to: Key): string {
  const chord = parseChord(text)
  if (!chord) return text
  const shift = pitchOf(to.tonic) - pitchOf(from.tonic)
  const move = (note: Note) => spellPitch(mod12(pitchOf(note) + shift), to)
  return chordName({
    root: move(chord.root),
    quality: chord.quality,
    bass: chord.bass ? move(chord.bass) : null,
  })
}

/** The key reached by moving `semitones` from `key`, spelled conventionally. */
export function transposeKey(key: Key, semitones: number): Key {
  const pitch = mod12(pitchOf(key.tonic) + semitones)
  const majorPitch = key.minor ? mod12(pitch + 3) : pitch
  // The common spelling (the picker's), keeping the source's side of the
  // circle for the one pitch where both are in use: F♯ and G♭.
  const majorName =
    majorPitch === 6 ? (usesFlats(key) ? 'Gb' : 'F#') : (COMMON_KEYS[majorPitch] ?? 'C')
  const major = parseNote(majorName) ?? { letter: 'C', accidental: 0 }
  if (!key.minor) return { tonic: major, minor: false }
  // The relative minor's tonic is the sixth degree of its major.
  return { tonic: spellInKey(pitch, { tonic: major, minor: false }, 5), minor: true }
}

// ---- Nashville numbers ------------------------------------------------------------

/**
 * A chord as a Nashville number in a key: "1", "4", "5/7", "6m", "♭7".
 * Key-independent by definition — which is why bands read it: the same chart
 * works whatever key the leader calls.
 */
export function nashville(text: string, key: Key): string {
  const chord = parseChord(text)
  if (!chord) return text
  const degree = (note: Note) => {
    const fromTonic = mod12(pitchOf(note) - pitchOf(key.tonic))
    const steps = key.minor ? [0, 2, 3, 5, 7, 8, 10] : MAJOR_STEPS
    const exact = steps.indexOf(fromTonic)
    if (exact >= 0) return String(exact + 1)
    const above = steps.findIndex((s) => s > fromTonic)
    return `b${(above === -1 ? 0 : above) + 1}`
  }
  return `${degree(chord.root)}${chord.quality}${chord.bass ? `/${degree(chord.bass)}` : ''}`
}

// ---- capo --------------------------------------------------------------------------

/**
 * With a capo on fret `capo`, the shapes a guitarist plays for a song in
 * `concert`: the key `capo` semitones below. Capo 2 in A plays G shapes.
 */
export function shapesKey(concert: Key, capo: number): Key {
  return transposeKey(concert, -capo)
}

/** The capo fret that lets `shapes` sound in `concert` (0–11). */
export function capoFor(concert: Key, shapes: Key): number {
  return mod12(pitchOf(concert.tonic) - pitchOf(shapes.tonic))
}

/**
 * The colours a song's arrangements take, in the order they were made, so
 * they can be told apart at a glance in a list: green, yellow, orange, and
 * round the wheel. A ninth starts the round again.
 */
export const ARRANGEMENT_SWATCHES = [
  'green',
  'yellow',
  'orange',
  'red',
  'pink',
  'violet',
  'blue',
  'teal',
] as const
export type ArrangementSwatch = (typeof ARRANGEMENT_SWATCHES)[number]

/** The colour of a song's arrangement at `index` (oldest first). */
export function arrangementSwatch(index: number): ArrangementSwatch {
  const n = ARRANGEMENT_SWATCHES.length
  return ARRANGEMENT_SWATCHES[((Math.trunc(index) % n) + n) % n] ?? 'green'
}

/**
 * A day's colour, so the days of a plan's times tell apart at a glance:
 * Monday green, Tuesday yellow… Sunday blue (the arrangement pastels).
 * `date` is an ISO day, "2026-10-25".
 */
export function weekdaySwatch(date: string): ArrangementSwatch {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay()
  // getUTCDay: 0 is Sunday; Monday starts the round.
  return ARRANGEMENT_SWATCHES[(day + 6) % 7] ?? 'green'
}
